import React, { useState, useEffect, useRef } from 'react';
import WebcamFeed from '../components/WebcamFeed';
import GameHUD from '../components/GameHUD';
import FeedbackOverlay from '../components/FeedbackOverlay';
import { detectJump } from '../utils/poseCalculations';
import { Camera, User } from 'lucide-react';

const JumpGame = ({ onGameEnd }) => {
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [timer, setTimer] = useState(60);
  const [restTime, setRestTime] = useState(20);
  const [isResting, setIsResting] = useState(true);
  const [round, setRound] = useState(1);
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [viewMode, setViewMode] = useState('camera');
  const previousPoseRef = useRef(null);
  const lastJumpTimeRef = useRef(0);
  const totalMovesRef = useRef(0);
  const correctMovesRef = useRef(0);
  const audioContextRef = useRef(null);
  const musicAudioRef = useRef(null);
  const scoreRef = useRef(score);
  const accuracyRef = useRef(accuracy);
  const onGameEndRef = useRef(onGameEnd);
  scoreRef.current = score;
  accuracyRef.current = accuracy;
  onGameEndRef.current = onGameEnd;

  useEffect(() => {
    // Start music once on first GET READY countdown
    if (isResting && round === 1) {
      playBackgroundMusic();
    }

    if (isResting) {
      const restInterval = setInterval(() => {
        setRestTime((prev) => {
          if (prev <= 1) {
            clearInterval(restInterval);
            setIsResting(false);
            setTimer(60);
            return 20;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(restInterval);
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          if (round === 1) {
            setIsResting(true);
            setRound(2);
            setRestTime(20);
          } else {
            stopBackgroundMusic();
            onGameEndRef.current?.({
              game_type: 'jump',
              score: scoreRef.current,
              accuracy: accuracyRef.current,
              duration: 120
            });
          }
          return 60;
        }
        return prev - 1;
      });
    }, 1000);

    generatePlatforms();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResting, round]);

  const generatePlatforms = () => {
    const newPlatforms = [];
    for (let i = 0; i < 5; i++) {
      newPlatforms.push({
        id: Date.now() + i,
        x: i * 18 + 10,
        y: 70 - (i % 2) * 15
      });
    }
    setPlatforms(newPlatforms);
  };

  const playBackgroundMusic = () => {
    try {
      musicAudioRef.current = new Audio('https://assets.mixkit.co/music/preview/mixkit-games-worldbeat-466.mp3');
      musicAudioRef.current.loop = true;
      musicAudioRef.current.volume = 0.3;
      musicAudioRef.current.play().catch(e => console.log('Music autoplay blocked:', e));
    } catch (error) {
      console.log('Failed to play music:', error);
    }
  };

  const stopBackgroundMusic = () => {
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      musicAudioRef.current = null;
    }
  };

  const playJumpSound = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 600;
    gainNode.gain.value = 0.15;
    
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.15);
  };

  const handlePoseUpdate = (landmarks) => {
    const now = Date.now();
    if (now - lastJumpTimeRef.current < 500) return;

    if (previousPoseRef.current) {
      const jumpResult = detectJump(landmarks, previousPoseRef.current);

      if (jumpResult.detected) {
        lastJumpTimeRef.current = now;
        totalMovesRef.current++;
        correctMovesRef.current++;

        const points = Math.floor(jumpResult.height * 500) + (combo * 15);
        setScore((prev) => prev + points);
        setCombo((prev) => prev + 1);
        
        const newAccuracy = (correctMovesRef.current / totalMovesRef.current) * 100;
        setAccuracy(newAccuracy);

        if (jumpResult.height > 0.15) {
          setFeedback('perfect');
        } else if (jumpResult.height > 0.1) {
          setFeedback('excellent');
        } else {
          setFeedback('good');
        }
        
        setShowFeedback(true);
        playJumpSound();
        
        setTimeout(() => setShowFeedback(false), 500);
        setTimeout(() => setCombo(0), 2000);
      }
    }

    previousPoseRef.current = landmarks;
  };

  return (
    <div className="relative w-full h-screen bg-black overflow-hidden">
      <WebcamFeed onPoseUpdate={handlePoseUpdate} isActive={!isResting} cameraOpacity={0.3} viewMode={viewMode} />
      
      {isResting ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-md">
          <div className="text-center">
            <div className="mb-8">
              <div className="text-6xl mb-4 animate-pulse">🐰</div>
              <h2 className="font-heading text-4xl font-black uppercase text-primary mb-2">
                {round === 1 ? 'GET READY!' : 'REST TIME!'}
              </h2>
              <p className="text-muted-foreground font-body text-lg uppercase tracking-wider">
                {round === 1 ? 'Prepare for Jump Master' : 'Round 1 Complete! Get Ready for Round 2'}
              </p>
            </div>
            
            <div className="bg-muted/20 rounded-xl p-8 backdrop-blur-xl border border-primary/30">
              <div className="text-7xl font-heading font-black text-accent mb-2">
                0:{restTime.toString().padStart(2, '0')}
              </div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">Rest Time</div>
            </div>

            {round === 1 && (
              <div className="mt-8 space-y-3 text-left max-w-md mx-auto">
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">⬆️</span>
                  <span className="font-body">Jump high to the rhythm</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">⚡</span>
                  <span className="font-body">Higher jumps = more points</span>
                </div>
                <div className="flex items-center gap-3 text-foreground">
                  <span className="text-2xl">🎯</span>
                  <span className="font-body">2 rounds × 1 minute each</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-black/60 backdrop-blur-xl border border-accent/50 rounded-lg px-6 py-2 z-30">
            <div className="text-accent font-heading text-lg uppercase tracking-wider">
              ROUND {round} / 2
            </div>
          </div>

          <div className="absolute top-4 left-4 z-30">
            <button
              onClick={() => setViewMode(prev => prev === 'camera' ? 'avatar' : 'camera')}
              className="flex items-center gap-2 bg-black/70 backdrop-blur-xl border border-white/20 rounded-lg px-4 py-2 transition-all duration-200 hover:border-primary/50 hover:bg-black/80 active:scale-95"
            >
              {viewMode === 'avatar' ? (
                <>
                  <User className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-[#00E5FF]">Avatar</span>
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 text-primary" />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-primary">Camera</span>
                </>
              )}
            </button>
          </div>

          <div className="absolute bottom-20 left-0 right-0 h-40">
            {platforms.map((platform, idx) => (
              <div
                key={platform.id}
                className="absolute w-24 h-4 bg-gradient-to-r from-primary to-accent rounded-sm"
                style={{
                  left: `${platform.x}%`,
                  bottom: `${platform.y}%`,
                  boxShadow: '0 0 15px rgba(0,240,255,0.5)',
                  animation: `pulse-neon ${2 + idx * 0.2}s ease-in-out infinite`
                }}
                data-testid="jump-platform"
              />
            ))}
          </div>

          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-center">
            <div className="text-accent font-heading text-lg uppercase tracking-wider animate-pulse-neon">
              JUMP TO THE BEAT!
            </div>
          </div>

          <GameHUD score={score} combo={combo} accuracy={accuracy} timer={timer} />
          <FeedbackOverlay feedback={feedback} visible={showFeedback} />
        </>
      )}
    </div>
  );
};

export default JumpGame;
